import React, { useContext, useState } from "react";
import ReactGA from "react-ga4";

import { IonAlert, IonBadge, IonContent, IonIcon, IonItem, IonLabel, IonList, IonModal, IonNote, IonToggle, ToggleChangeEventDetail, ToggleCustomEvent, useIonRouter } from "@ionic/react";
import { t } from "i18next";
import { exitOutline, fileTrayStackedOutline, key, mailOpen, mailOutline, trash } from "ionicons/icons";
import { config } from "../../app.config";
import BasicTemplate from "../../components/PageTemplates/BasicTemplate";
import { ApplicationContext } from "../../contexts/ApplicationContext";
import { AuthenticatedUserContext } from "../../contexts/AuthenticatedUserContext";
import { ApiService } from "../../services";
import { ChangeEmailModal } from "./ChangeEmailModal";
import { ChangePasswordModal } from "./ChangePasswordModal";

export const Account = (props: any) => {
  const { member, showAllCalendarEvents, showAllFiles, setShowAllCalendarEvents, setShowAllFiles } = useContext(AuthenticatedUserContext);
  const [showConfirm, setShowConfirm] = useState(false);
  const { setShowSignOutConfirm } = useContext(AuthenticatedUserContext);
  const changePasswordModal = React.useRef<HTMLIonModalElement | null>(null);
  const [didChangePassword, setDidChangePassword] = useState<boolean>(false);
  const changeEmailModal = React.useRef<HTMLIonModalElement | null>(null);
  const [didChangeEmail, setDidChangeEmail] = useState<boolean>(false);
  const { signOut, numUnreadFiles } = useContext(AuthenticatedUserContext);
  const { application } = useContext(ApplicationContext);
  const router = useIonRouter();
  const version = config.version;

  return (
    <BasicTemplate hideHeader={false} title={t("myAccount") || ""} hideBackButton={false}>
      <IonContent fullscreen={true} class={"ion-padding"} color={"light"}>
        <IonList inset={true}>
          <IonItem routerLink={"/my-files"}>
            <IonIcon icon={fileTrayStackedOutline} slot={"start"} />
            <IonLabel>{t("myFiles")}</IonLabel>
            {numUnreadFiles <= 0 ? (
              ""
            ) : (
              <IonBadge color={"danger"} slot={"end"}>
                {numUnreadFiles}
              </IonBadge>
            )}
          </IonItem>
        </IonList>

        {application.contact_enabled ? (
          <IonList inset={true}>
            <IonItem button routerLink={"/contact"}>
              <IonIcon icon={mailOpen} slot={"start"} />
              <IonLabel>{t("helpdesk")}</IonLabel>
            </IonItem>
          </IonList>
        ) : (
          ""
        )}

        {member?.is_admin ? (
          <>
            <div className={"inset-list"}>
              <IonLabel className={"inset-list-header"}>{t("adminViewOptions")}</IonLabel>
              <IonLabel class={"ion-text-wrap"}>{t("adminViewOptionsExplainer")}</IonLabel>
            </div>

            <IonList inset={true}>
              <IonItem>
                <IonLabel>{t("showAllCalendarEvents")}</IonLabel>
                <IonToggle
                  slot={"end"}
                  onIonChange={(e: ToggleCustomEvent<ToggleChangeEventDetail>) => {
                    setShowAllCalendarEvents(e.detail.checked);
                  }}
                  checked={showAllCalendarEvents}
                ></IonToggle>
              </IonItem>

              <IonItem>
                <IonLabel>{t("showAllFiles")}</IonLabel>
                <IonToggle
                  slot={"end"}
                  onIonChange={(e: ToggleCustomEvent<ToggleChangeEventDetail>) => {
                    setShowAllFiles(e.detail.checked);
                  }}
                  checked={showAllFiles}
                ></IonToggle>
              </IonItem>
            </IonList>
          </>
        ) : (
          ""
        )}

        <div className={"inset-list"}>
          <IonLabel className={"inset-list-header"}>{t("signInDetails")}</IonLabel>
        </div>

        <IonList inset={true}>
          <IonItem button id={"change-email"}>
            <IonIcon icon={mailOutline} slot={"start"} />
            <IonLabel>{t("changeEmail")}</IonLabel>
          </IonItem>

          <IonItem button id={"change-password"}>
            <IonIcon icon={key} slot={"start"} />
            <IonLabel>{t("changePassword")}</IonLabel>
          </IonItem>

          <IonItem
            button
            onClick={() => {
              setShowSignOutConfirm(true);
            }}
          >
            <IonIcon icon={exitOutline} slot={"start"} />
            <IonLabel>{t("signOut")}</IonLabel>
          </IonItem>
        </IonList>

        <IonList inset={true}>
          <IonItem
            button
            onClick={() => {
              setShowConfirm(true);
            }}
          >
            <IonIcon icon={trash} slot={"start"} color={"danger"} />
            <IonLabel
              style={{
                "--color": "var(--ion-color-danger)",
              }}
            >
              {t("deleteMyData")}
            </IonLabel>
          </IonItem>
        </IonList>

        <IonNote
          className={"ion-padding"}
          style={{
            display: "block",
            textAlign: "center",
          }}
        >
          {t("operatedBy")} {t("$appName")}. {t("operatedBy2")}
        </IonNote>

        <IonNote
          className={"ion-padding"}
          style={{
            paddingTop: 0,
            display: "block",
            textAlign: "center",
          }}
        >
          Belong v{version}
        </IonNote>
      </IonContent>

      <IonAlert
        cssClass={"ion-alert-danger"}
        isOpen={showConfirm}
        header={t("areYouSure") || ""}
        message={t("deleteDataConfirm") || ""}
        buttons={[
          {
            text: t("cancel"),
            role: "cancel",
            handler: () => {
              setShowConfirm(false);
            },
          },
          {
            text: t("deleteMyData"),
            handler: () => {
              setShowConfirm(false);

              ApiService.delete("members/" + member?.id)
                .then(() => {
                  signOut().then(() => {
                    router.push("/");
                    window.location.reload();
                  });
                })
                .catch(() => {
                  alert("We're sorry but an error occurred. Please contact us on " + config.contact.email + " and we'll help you out.");
                });

              ReactGA.event({
                category: "user",
                action: "deleteData",
                label: t("appName") || "",
              });
            },
          },
        ]}
      />
      <IonAlert
        isOpen={didChangePassword}
        header={t("passwordChanged") || ""}
        message={t("passwordChangedSuccess") || ""}
        buttons={[
          {
            text: t("Ok"),
            handler: () => {
              setDidChangePassword(false);
              changePasswordModal.current?.dismiss();
            },
          },
        ]}
      />
      <IonModal ref={changePasswordModal} trigger={"change-password"}>
        <ChangePasswordModal
          onDismiss={() => {
            changePasswordModal.current?.dismiss();
          }}
          onSuccess={() => {
            setDidChangePassword(true);
          }}
        />
      </IonModal>
      <IonAlert
        isOpen={didChangeEmail}
        header={t("emailChanged") || ""}
        message={t("emailChangedSuccess") || ""}
        buttons={[
          {
            text: t("Ok"),
            handler: () => {
              setDidChangeEmail(false);
              changeEmailModal.current?.dismiss();
            },
          },
        ]}
      />
      <IonModal ref={changeEmailModal} trigger={"change-email"}>
        <ChangeEmailModal
          onDismiss={() => {
            changeEmailModal.current?.dismiss();
          }}
          onSuccess={() => {
            setDidChangeEmail(true);
          }}
        />
      </IonModal>
    </BasicTemplate>
  );
};
